/**
 * Timestamps from the backend are UTC with no offset in the string
 * (MySQL stores UTC; `2026-09-03T18:33:40`). `new Date()` would read that as
 * the viewer's local time, so it has to be marked UTC before formatting.
 *
 * The clinic works in Israel, so times are shown in Asia/Jerusalem regardless
 * of where the browser is.
 */

const CLINIC_TIME_ZONE = "Asia/Jerusalem";

const asUtcDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  // Add the Z only when the string carries no zone of its own.
  const hasZone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso);
  const date = new Date(hasZone ? iso : `${iso}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** e.g. "03/09/2026, 21:33" — the DD/MM/YYYY the rest of the app uses. */
export const formatClinicTime = (iso: string | null | undefined): string => {
  const date = asUtcDate(iso);
  if (!date) return "";
  return date.toLocaleString("en-GB", {
    timeZone: CLINIC_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** "now", "12 min ago", "3 h ago", then the full date. */
export const formatClinicRelative = (
  iso: string | null | undefined,
  lang: "he" | "en" = "he"
): string => {
  const date = asUtcDate(iso);
  if (!date) return "";
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return lang === "he" ? "עכשיו" : "now";
  if (minutes < 60) return lang === "he" ? `לפני ${minutes} דק׳` : `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return lang === "he" ? `לפני ${hours} שע׳` : `${hours} h ago`;
  return formatClinicTime(iso);
};
